import type { AccountInfo, AuthenticationResult } from '@azure/msal-browser';

import { config } from '../lib/config';
import { logger } from '../lib/logger';

export const API_SCOPE = config.azure.apiAppIdUri.replace(/\/$/, '') + '/access_as_user';
export const API_LOGIN_REQUEST = { scopes: [API_SCOPE] };

/**
 * Dedicated lightweight path for the MSAL popup auth callback.
 * The SWA navigation fallback serves index.html for this path, so the SPA
 * loads but main.tsx detects the path early and skips the full app bootstrap.
 *
 * IMPORTANT: this URI must also be added to the Azure AD app registration as
 * an allowed redirect URI (Authentication → Single-page application redirect URIs).
 */
export const MSAL_POPUP_REDIRECT_PATH = '/auth/callback';

/**
 * Returns the full absolute redirect URI for the MSAL popup auth callback.
 * Use this wherever a `redirectUri` is passed to MSAL APIs so the path is
 * always constructed consistently.
 */
export function getMsalPopupRedirectUri(): string {
  return window.location.origin + MSAL_POPUP_REDIRECT_PATH;
}

export interface MsalBootstrapInstance {
  initialize: () => Promise<void>;
  handleRedirectPromise: () => Promise<AuthenticationResult | null>;
  setActiveAccount: (account: AccountInfo | null) => void;
  getActiveAccount: () => AccountInfo | null;
  getAllAccounts: () => AccountInfo[];
}

function hasMsalCallbackParams(hash: string = window.location.hash): boolean {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  return params.has('code') || params.has('error') || params.has('state');
}

/**
 * Extract key diagnostic fields from an MSAL error (or any Error) into a plain
 * serialisable object so the logger can include them in console output.
 *
 * MSAL throws BrowserAuthError / ServerError instances whose `message`, `stack`
 * and custom properties (errorCode, subError, correlationId) are typically
 * non-enumerable, which means they are silently dropped when the logger's
 * maskSensitiveData helper iterates Object.entries(). This helper copies them
 * into an enumerable record so they survive the masking step.
 */
export function formatMsalError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    const msalErr = error as Error & {
      errorCode?: string;
      subError?: string;
      correlationId?: string;
    };
    return {
      name: error.name,
      message: error.message,
      errorCode: msalErr.errorCode,
      subError: msalErr.subError,
      correlationId: msalErr.correlationId,
      stack: error.stack,
    };
  }
  return { raw: String(error) };
}

export async function initializeMsalAuthentication(instance: MsalBootstrapInstance): Promise<void> {
  logger.debug('Initializing primary MSAL instance', { hasCallbackParams: hasMsalCallbackParams() }, 'AUTH');

  await instance.initialize();

  try {
    const response = await instance.handleRedirectPromise();
    if (response?.account) {
      instance.setActiveAccount(response.account);
      logger.authSuccess('MSAL callback handling', response.account.username);
    } else {
      logger.debug('No pending MSAL callback detected', { accountCount: instance.getAllAccounts().length }, 'AUTH');
    }
  } catch (error) {
    logger.authError('MSAL callback handling', formatMsalError(error));
  }

  if (!instance.getActiveAccount()) {
    const existingAccount = instance.getAllAccounts()[0] ?? null;
    if (existingAccount) {
      instance.setActiveAccount(existingAccount);
      logger.debug('Restored existing MSAL account as active account', { username: existingAccount.username }, 'AUTH');
    }
  }
}
